import React, { useEffect, useRef, useState } from 'react';
import PartnerRemoteSelect from './components/PartnerRemoteSelect';
import toast from 'react-hot-toast';
import { FiArrowRight } from 'react-icons/fi';
import CmrFileDropzone from './_blocks/CmrFileDropzone';
import CmrExtractionProgress from './CmrExtractionProgress';
import { extractCmrFromFile } from '../../services/cmrExtractionService';
import { isPdfFile } from '../../utils/cmrFileProcessing';
import useOnboardingPersistence from '../../hooks/useOnboardingPersistence';
import {
  CMR_SUB_STEPS,
  getMobileFromCmrExtraction,
  persistCmrExtraction,
} from '../../utils/investorOnboardingPersistence';
import { saveCmrFileToIndexedDB } from '../../utils/cmrUploadFileStore';

const EXTRACTION_COMPLETE_DELAY_MS = 1400;

function InputField({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      {label && <label className="text-sm font-medium text-gray-700">{label}</label>}
      {children}
    </div>
  );
}

function StyledInput({ value, onChange, placeholder, maxLength }) {
  return (
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      maxLength={maxLength}
      className="w-full px-3.5 py-2.5 rounded-lg border text-sm transition-all outline-none bg-white text-gray-900 border-gray-300 hover:border-green-400 focus:border-green-500 focus:ring-2 focus:ring-green-500/10"
    />
  );
}

function GreenButton({ onClick, disabled, loading, children }) {
  const inactive = disabled || loading;
  const stateClass = loading
    ? 'bg-green-600 text-white cursor-wait shadow-sm'
    : disabled
      ? 'bg-gray-200 text-gray-500 cursor-not-allowed shadow-none'
      : 'bg-green-600 text-white hover:bg-green-700 active:scale-[0.99] shadow-sm hover:shadow-md';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={inactive}
      className={`w-full py-2.5 px-4 rounded-lg font-semibold text-sm transition-all ${stateClass}`}
    >
      {loading ? (
        <span className="flex items-center justify-center gap-2">
          <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" aria-hidden>
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          Processing...
        </span>
      ) : (
        children
      )}
    </button>
  );
}

export default function AutomatedCmrStartStep({
  selectedIfaId,
  onSelectIfa,
  mobile,
  onMobileChange,
  onStart,
  starting,
}) {
  const { state, patch } = useOnboardingPersistence();
  const cmrSubStep = state.cmrSubStep;
  const storedExtraction = state.cmrExtraction;

  const [cmrFile, setCmrFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [apiFinished, setApiFinished] = useState(false);
  const [apiSuccess, setApiSuccess] = useState(false);
  const mobileEditedByUserRef = useRef(false);

  useEffect(() => {
    if (storedExtraction && cmrSubStep === CMR_SUB_STEPS.PARTNER) {
      const extractedMobile = getMobileFromCmrExtraction(storedExtraction);
      if (extractedMobile && !mobile && !mobileEditedByUserRef.current) {
        onMobileChange(extractedMobile);
      }
    }
  }, [storedExtraction, cmrSubStep, mobile, onMobileChange]);

  const handleMobileChange = (rawValue) => {
    mobileEditedByUserRef.current = true;
    onMobileChange(String(rawValue || '').replace(/\D/g, ''));
  };

  const handleContinue = async (e) => {
    e?.preventDefault?.();
    if (!cmrFile) return;

    mobileEditedByUserRef.current = false;
    setExtracting(true);
    setApiFinished(false);
    setApiSuccess(false);

    try {
      const result = await extractCmrFromFile(cmrFile);
      persistCmrExtraction({
        ...result,
        _upload_file_name: cmrFile.name,
      });
      await saveCmrFileToIndexedDB(cmrFile, null);
      setApiSuccess(true);
      setApiFinished(true);

      await new Promise((r) => setTimeout(r, EXTRACTION_COMPLETE_DELAY_MS));

      const extractedMobile = getMobileFromCmrExtraction(result);
      if (extractedMobile) {
        onMobileChange(extractedMobile);
      }
      patch({ cmrSubStep: CMR_SUB_STEPS.PARTNER });
    } catch (err) {
      setApiFinished(true);
      setApiSuccess(false);
      toast.error(err?.response?.data?.message || err?.message || 'CMR extraction failed.');
    } finally {
      setExtracting(false);
      setApiFinished(false);
      setApiSuccess(false);
    }
  };

  const canStart = selectedIfaId && mobile.length === 10;

  if (extracting && cmrFile) {
    return (
      <CmrExtractionProgress
        fileName={cmrFile.name}
        isPdf={isPdfFile(cmrFile)}
        apiFinished={apiFinished}
        apiSuccess={apiSuccess}
      />
    );
  }

  if (cmrSubStep === CMR_SUB_STEPS.UPLOAD) {
    return (
      <div className="flex flex-col gap-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">CMR Upload & Partner Setup</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Upload the investor&apos;s CMR document. We will extract details automatically before you select the partner.
          </p>
        </div>

        <div className="flex flex-col gap-5">
          <CmrFileDropzone file={cmrFile} onFileChange={setCmrFile} label="Upload CMR" />

          <GreenButton onClick={handleContinue} disabled={!cmrFile}>
            Continue <FiArrowRight className="inline ml-1" />
          </GreenButton>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">CMR Upload & Partner Setup</h3>
        <p className="text-sm text-gray-500 mt-0.5">
          CMR details extracted. Choose the partner and confirm the mobile number to start onboarding.
        </p>
        {storedExtraction?._upload_file_name && (
          <p className="text-xs text-emerald-700 mt-2 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
            Extracted from: {storedExtraction._upload_file_name}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-5">
        <InputField label="Partner">
          <PartnerRemoteSelect
            size="large"
            className="w-full"
            value={selectedIfaId}
            onChange={onSelectIfa}
          />
        </InputField>

        <InputField label="Investor Mobile">
          <StyledInput
            value={mobile}
            onChange={(e) => handleMobileChange(e.target.value)}
            placeholder="Enter 10-digit mobile number"
            maxLength={10}
          />
        </InputField>

        <GreenButton onClick={onStart} disabled={!canStart} loading={starting}>
          Start onboarding <FiArrowRight className="inline ml-1" />
        </GreenButton>
      </div>
    </div>
  );
}
